import {
  aiPricingFallbackKey,
  isAiCapability,
  type AiModelPricing,
  type Locale,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelledSubmit } from '@/shared/components/dynamic-form/cancelled-submit/cancelled-submit';
import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useConfirm } from '@/shared/hooks/useConfirm/useConfirm';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useAiPricing } from '../../hooks/useAiPricing/useAiPricing';
import {
  aiRateFields,
  aiRateInitialValues,
  aiRateSchema,
} from '../../utils/ai-rate-fields/ai-rate-fields.util';

export interface UseAiRateEditorModalLogicInput {
  /** The `ai.pricing` key being edited — a serializable modal payload. */
  settingKey: string;
  /** What to call the model on screen; falls back to the key. */
  label: string;
  /** Which capability the row belongs to, for the fallback prefill. */
  capability: string;
  onDone: () => void;
}

export interface UseAiRateEditorModalLogic {
  title: string;
  fields: readonly FieldDef[];
  schema: ReturnType<typeof aiRateSchema>;
  initialValues: Partial<AiModelPricing>;
  /** Held until the table arrives — RHF reads `defaultValues` exactly once. */
  isLoading: boolean;
  submit: (values: AiModelPricing) => Promise<void>;
}

/**
 * One model's four rates (U5: the modal below is presentation only).
 *
 * It loads `ai.pricing` itself rather than taking it as modal data, for the
 * reason `ProviderConfigModal` does: the payload stays a serializable key, and
 * an open dialog keeps agreeing with the server while the table refetches
 * underneath it.
 */
export function useAiRateEditorModalLogic({
  settingKey,
  label,
  capability,
  onDone,
}: UseAiRateEditorModalLogicInput): UseAiRateEditorModalLogic {
  const { t } = useTranslation('integrations');
  const { locale } = useDirection();
  const toast = useToast();
  const { confirm } = useConfirm();
  const pricing = useAiPricing();

  const rates = pricing.effective[settingKey] ?? null;
  const codedDefault = pricing.codedDefault[settingKey] ?? null;
  // What an unpriced model is being charged right now, so the form opens on the
  // real number rather than on zeros nobody meant.
  const fallback = isAiCapability(capability)
    ? (pricing.effective[aiPricingFallbackKey(capability)] ?? null)
    : null;

  const submit = useCallback(
    async (values: AiModelPricing) => {
      if (!pricing.mayManage) {
        // Shaped as an `ApiError` rather than thrown bare: the form renders a
        // thrown error's own message as its failure summary, and an internal
        // token would then reach the screen untranslated (U8). `status: 0` is
        // the literal truth — the request never left the browser.
        const refusal: ApiError = { status: 0, code: 'forbidden', message: t('rates.forbidden') };
        throw refusal;
      }

      // Read from the DEFINITION, never hardcoded: `ai.pricing` is marked
      // dangerous in the registry because a wrong rate silently mis-enforces the
      // budget guard, and one key must not obey two different rules depending on
      // which screen wrote it (W8).
      if (pricing.definition?.dangerous === true) {
        const accepted = await confirm({
          title: t('rates.danger.title'),
          description: t('rates.danger.message', { model: label }),
          confirmLabel: t('rates.danger.confirm'),
          tone: 'warning',
        });

        // The sentinel, not a bare Error: a declined confirmation is `cancelled`
        // and prints nothing, where any other rejection is mapped onto the form
        // as a refusal.
        if (!accepted) throw new CancelledSubmit();
      }

      await pricing.saveRate(settingKey, values);
      toast.success(t('rates.edit.saved', { model: label }));
      onDone();
    },
    [confirm, label, onDone, pricing, settingKey, t, toast],
  );

  return useMemo(
    () => ({
      title: t('rates.edit.title', { model: label }),
      fields: aiRateFields(t, codedDefault, locale satisfies Locale),
      schema: aiRateSchema(),
      initialValues: aiRateInitialValues(rates, fallback),
      isLoading: pricing.state === 'loading',
      submit,
    }),
    [codedDefault, fallback, label, locale, pricing.state, rates, submit, t],
  );
}
